import { useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { apiPost } from '../lib/api';
import type { Schema } from '../../amplify/data/resource';
import { Button, useToast } from './ui';
type Client = Schema['Client']['type'];

export default function Billing() {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [clients,   setClients]   = useState<Client[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState<string>('');
  const [amount,    setAmount]    = useState('');
  const [desc,      setDesc]      = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms,   setSendSms]   = useState(false);
  const [phone,     setPhone]     = useState('');
  const [sending,    setSending]    = useState(false);
  const [lastUrl,    setLastUrl]    = useState<string | null>(null);
  const [sendErrors, setSendErrors] = useState<string[]>([]);

  // Auto-charge state
  const [acAmount,     setAcAmount]     = useState('');
  const [acDesc,       setAcDesc]       = useState('');
  const [acLoading,    setAcLoading]    = useState(false);
  const [acResult,     setAcResult]     = useState<{ invoiceId: string; hostedUrl?: string } | null>(null);

  const { show, ToastEl } = useToast();

  useEffect(() => {
    db.models.Client.list().then(({ data }) => {
      setClients(data.sort((a, b) => (a.clientName ?? '').localeCompare(b.clientName ?? '')));
      setLoading(false);
    }).catch(() => setLoading(false));

    // Default description to current month
    setDesc(`IntentFlo — Services ${new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}`);
  }, []);

  const client = clients.find(c => c.id === selected) ?? null;

  async function handleSend() {
    if (!client) { show('Select a client first', 'error'); return; }
    const dollars = parseFloat(amount);
    if (isNaN(dollars) || dollars <= 0) { show('Enter a valid amount', 'error'); return; }
    if (dollars < 0.50) { show('Minimum invoice amount is $0.50', 'error'); return; }
    setSending(true);
    setLastUrl(null);
    setSendErrors([]);
    try {
      const res = await apiPost<{ url: string; errors?: string[] }>('/billing/send-bill', {
        clientId:       client.id,
        amountDollars:  dollars,
        description:    desc || 'IntentFlo — Balance Due',
        sendEmail,
        recipientEmail: sendEmail ? (client.contactEmail ?? '') : undefined,
        recipientName:  sendEmail ? (client.clientName  ?? '') : undefined,
        sendSms,
        recipientPhone: sendSms ? phone : undefined,
      });
      setLastUrl(res.url);
      // persist balance on client record
      await db.models.Client.update({
        id: client.id,
        ...{ balanceDue: String(dollars), billingStatus: 'past_due' } as any,
      });
      if (res.errors?.length) {
        setSendErrors(res.errors);
        show('Bill created — but some delivery failed (see warnings below)', 'error');
      } else {
        show(`Bill sent to ${client.clientName}!`);
      }
      // reset amount only; keep client + desc selected for quick re-use
      setAmount('');
    } catch (e: any) {
      show('Failed: ' + e.message, 'error');
    } finally {
      setSending(false);
    }
  }

  async function handleAutoCharge() {
    if (!client) { show('Select a client first', 'error'); return; }
    if (!(client as any).autopayEnabled) { show('Client does not have auto-pay enabled', 'error'); return; }
    const dollars = parseFloat(acAmount);
    if (isNaN(dollars) || dollars <= 0) { show('Enter a valid amount', 'error'); return; }
    setAcLoading(true);
    setAcResult(null);
    try {
      const res = await apiPost<{ invoiceId: string; hostedUrl?: string }>('/billing/auto-charge', {
        clientId:      client.id,
        amountDollars: dollars,
        description:   acDesc || 'IntentFlo — Monthly Services',
      });
      setAcResult(res);
      // Optimistically update balance
      await db.models.Client.update({ id: client.id, ...{ balanceDue: String(dollars), billingStatus: 'past_due' } as any });
      show(`Auto-charge invoice created for ${client.clientName}!`);
      setAcAmount('');
    } catch (e: any) {
      show('Auto-charge failed: ' + e.message, 'error');
    } finally {
      setAcLoading(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', background: '#080c14', color: '#e8edf5',
    border: '1px solid #1e2d42', borderRadius: 8,
    padding: '10px 13px', fontSize: 14, fontFamily: 'Sora, sans-serif',
    outline: 'none', boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, color: '#4a5d7a',
    letterSpacing: '0.08em', display: 'block', marginBottom: 6,
  };

  return (
    <div>
      {ToastEl}

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#e8edf5' }}>Billing</h1>
        <p style={{ color: '#3b4f6a', fontSize: 13, marginTop: 4 }}>
          Send invoices to any client via Stripe Checkout
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 900 }}>

        {/* ── Left: form ─────────────────────────────────────────── */}
        <div style={{
          background: '#0b1220', border: '1px solid #162035',
          borderRadius: 14, padding: 28, display: 'flex', flexDirection: 'column', gap: 18,
        }}>
          {/* Client selector */}
          <div>
            <label style={labelStyle}>CLIENT</label>
            {loading ? (
              <div style={{ color: '#3b4f6a', fontSize: 13 }}>Loading clients…</div>
            ) : (
              <select
                value={selected}
                onChange={e => setSelected(e.target.value)}
                style={{
                  ...inputStyle,
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234a5d7a' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  paddingRight: 36,
                  cursor: 'pointer',
                }}
              >
                <option value=''>— Select a client —</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.clientName}{(c as any).clientType ? ` (${(c as any).clientType?.toUpperCase()})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Amount */}
          <div>
            <label style={labelStyle}>AMOUNT DUE ($) *</label>
            <input
              type="number" min="0.50" step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="500.00"
              style={inputStyle}
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>DESCRIPTION</label>
            <input
              type="text"
              value={desc}
              onChange={e => setDesc(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Delivery options */}
          <div>
            <label style={labelStyle}>DELIVERY</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#8a9bb5' }}>
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
                  style={{ accentColor: '#3b82f6', width: 14, height: 14 }} />
                Send via email
                {sendEmail && client?.contactEmail && (
                  <span style={{ fontSize: 11, color: '#4a5d7a' }}>→ {client.contactEmail}</span>
                )}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#8a9bb5' }}>
                <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)}
                  style={{ accentColor: '#3b82f6', width: 14, height: 14 }} />
                Send via SMS
              </label>
            </div>
          </div>

          {sendSms && (
            <div>
              <label style={labelStyle}>PHONE NUMBER</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+15551234567"
                style={inputStyle}
              />
            </div>
          )}

          {sendErrors.length > 0 && (
            <div style={{
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8, padding: '12px 14px',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', marginBottom: 6 }}>
                DELIVERY WARNINGS
              </div>
              {sendErrors.map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: '#fca5a5' }}>{e}</div>
              ))}
            </div>
          )}

          <Button variant="primary" loading={sending} onClick={handleSend}
            style={{ marginTop: 4, width: '100%', padding: '12px 0', fontSize: 14, fontWeight: 700 }}>
            🧾 Send Bill
          </Button>
        </div>

        {/* ── Right: client summary + last invoice ───────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Client card */}
          <div style={{
            background: '#0b1220', border: '1px solid #162035',
            borderRadius: 14, padding: 24,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 14 }}>
              CLIENT SUMMARY
            </div>
            {!client ? (
              <div style={{ fontSize: 13, color: '#3b4f6a' }}>Select a client to see details</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#e8edf5' }}>{client.clientName}</div>
                {client.contactEmail && (
                  <div style={{ fontSize: 12, color: '#4a5d7a' }}>📧 {client.contactEmail}</div>
                )}
                {(client as any).clientType && (
                  <div style={{ fontSize: 12, color: '#4a5d7a' }}>
                    Type: <span style={{ color: '#60a5fa', fontWeight: 600 }}>
                      {String((client as any).clientType).toUpperCase()}
                    </span>
                  </div>
                )}
                {(() => {
                  const bal       = Number((client as any).balanceDue ?? 0);
                  const rawStatus = (client as any).billingStatus as string | undefined;
                  const isPastDue = (rawStatus === 'past_due' || rawStatus === 'outstanding') && bal > 0;
                  const label     = isPastDue           ? 'PAST DUE'
                                  : bal === 0           ? 'PAID'
                                  : rawStatus           ? rawStatus.replace('_', ' ').toUpperCase()
                                  : null;
                  const color     = isPastDue ? '#f87171' : '#34d399';
                  return label ? (
                    <div style={{ fontSize: 12, color: '#4a5d7a' }}>
                      Status:{' '}
                      <span style={{ fontWeight: 600, color }}>{label}</span>
                    </div>
                  ) : null;
                })()}
                {(client as any).balanceDue && Number((client as any).balanceDue) > 0 && (
                  <div style={{ fontSize: 13, color: '#f87171', fontWeight: 600 }}>
                    Outstanding: ${Number((client as any).balanceDue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Last invoice link */}
          {lastUrl && (
            <div style={{
              background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)',
              borderRadius: 12, padding: 20,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#3b82f6', letterSpacing: '0.08em', marginBottom: 10 }}>
                INVOICE CREATED
              </div>
              <div style={{ fontSize: 13, color: '#8a9bb5', marginBottom: 14 }}>
                Stripe Checkout link (share manually if needed):
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  readOnly
                  value={lastUrl}
                  style={{
                    flex: 1, background: '#060a10', color: '#60a5fa',
                    border: '1px solid #1a2840', borderRadius: 7,
                    padding: '8px 10px', fontSize: 11, fontFamily: 'DM Mono, monospace',
                    outline: 'none',
                  }}
                  onClick={e => (e.target as HTMLInputElement).select()}
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(lastUrl); show('Link copied!'); }}
                  style={{
                    background: '#1a2840', border: '1px solid #1e3a6e', borderRadius: 7,
                    color: '#60a5fa', fontSize: 12, padding: '8px 14px', cursor: 'pointer',
                    fontFamily: 'Sora, sans-serif', flexShrink: 0,
                  }}
                >
                  Copy
                </button>
              </div>
              <a
                href={lastUrl} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', marginTop: 10, fontSize: 12, color: '#3b82f6' }}
              >
                Open Checkout →
              </a>
            </div>
          )}

          {/* Info */}
          <div style={{
            background: '#0b1220', border: '1px solid #162035',
            borderRadius: 12, padding: 20,
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#4a5d7a', letterSpacing: '0.08em', marginBottom: 10 }}>
              HOW IT WORKS
            </div>
            {[
              ['Stripe Checkout', 'A secure payment link is created and emailed / SMSed to the client.'],
              ['Balance updated', "The client's balance is marked as Past Due in the system."],
              ['Client portal', 'Clients can pay directly from their portal billing tab.'],
            ].map(([title, body]) => (
              <div key={title} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#8a9bb5' }}>{title}</div>
                <div style={{ fontSize: 12, color: '#3b4f6a' }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Auto-Charge Section (autopay-enabled clients only) ──────────────── */}
      {client && (client as any).autopayEnabled && (
        <div style={{ marginTop: 32, maxWidth: 900 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
            <div style={{
              background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)',
              borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#34d399',
            }}>AUTO-CHARGE</div>
            <span style={{ fontSize: 13, color: '#8a9bb5', fontWeight: 600 }}>
              Charge saved card automatically — no manual payment required
            </span>
          </div>

          <div style={{
            background: '#0b1220', border: '1px solid rgba(52,211,153,0.2)',
            borderRadius: 14, padding: 28, display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr auto', gap: 16, alignItems: 'end',
          }}>
            <div>
              <label style={labelStyle}>AMOUNT ($)</label>
              <input
                type="number" min="0.50" step="0.01"
                value={acAmount}
                onChange={e => setAcAmount(e.target.value)}
                placeholder="499.00"
                style={inputStyle}
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>DESCRIPTION</label>
              <input
                type="text"
                value={acDesc}
                onChange={e => setAcDesc(e.target.value)}
                placeholder={`IntentFlo — Services ${new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' })}`}
                style={inputStyle}
              />
            </div>
            <Button
              variant="primary"
              loading={acLoading}
              onClick={handleAutoCharge}
              style={{ background: '#059669', padding: '10px 20px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Run Auto-Charge →
            </Button>
          </div>

          {acResult && (
            <div style={{
              marginTop: 14, background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)',
              borderRadius: 10, padding: '14px 18px', display: 'flex', gap: 24, alignItems: 'center',
            }}>
              <div style={{ fontSize: 12, color: '#8a9bb5' }}>
                Invoice: <span style={{ color: '#34d399', fontFamily: 'DM Mono, monospace', fontWeight: 600 }}>{acResult.invoiceId}</span>
              </div>
              {acResult.hostedUrl && (
                <a href={acResult.hostedUrl} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: '#34d399', fontWeight: 600 }}>
                  View Invoice →
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
